import type { StatusColorName, StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  mdiCalendarClockOutline,
  mdiClipboardTextClockOutline,
  mdiClockOutline,
  mdiEyeCheckOutline,
} from "@mdi/js"
import type { ScheduleEventCommunicationPresentation } from "../../../../../../../../../../../../../api/kehrblatt/work-orders"
import { formatContactAddress } from "../../../../../../../../../../../_shared/_logic/contact-addresses"
import { contactPointKindLabel } from "../../../../../../../../../../../_shared/_logic/contact-point-kinds"
import {
  buildContactTag,
  groupContactPointsByCustomerId,
  type ContactTagPresentation,
} from "../../../../../../../../../../../_shared/_logic/contact-quality-tags"
import { buildScheduleEventCommunicationTagPresentation } from "../../../../../../../../../../../../../_shared/_logic/communication-presentation"
import { scheduleEventPriority } from "../../../../../../../../../../../../../_shared/_logic/outreach-presentation"
import { resolveOrderTypeColor } from "../../../../../../../../../../../../../_shared/_logic/order-type-tags"
import { formatAddressInline } from "../../../../../../../../../../../../../_shared/_logic/countries"
import {
  formatPropertyCustomerRole,
  normalizePropertyCustomerRole,
} from "../../../../../../../../../../../_shared/_logic/customer-roles"
import {
  formatPropertyUnitDisplay,
  formatPropertyUnitDisplayForProperty,
} from "../../../../../../../../../../../../../_shared/_logic/unit-labels"
import { formatDateOnlyDisplay } from "../../../../../../../../../../../../_shared/_logic/timezones"
import {
  internalOrderTypeLabel,
  orderTypeHeadingLabel,
} from "../../../../../../../../../../../../../_shared/_logic/order-type-labels"

export type WorkOrderSummaryFact = {
  label: string
  value: string
}

export type WorkOrderSummaryContact = {
  label: string
  roleLabel: string
  contactHint: string
  contactKinds: string[]
  contactChannels: WorkOrderSummaryContactChannel[]
}

export type WorkOrderSummaryContactChannel = {
  kind: string
  label: string
  tooltip: string
}

export type WorkOrderSummaryModel = {
  orderTag: {
    label: string
    numberLabel: string
    color: StatusColorName
  }
  statusTag: StatusDefinition<string>
  appointmentTag: StatusDefinition<string>
  durationTag: StatusDefinition<string>
  dueTag: StatusDefinition<string>
  propertyLabel: string
  unitLabel: string | null
  contactTags: ContactTagPresentation[]
  orderFacts: WorkOrderSummaryFact[]
  locationFacts: WorkOrderSummaryFact[]
  contactRows: WorkOrderSummaryContact[]
  facts: WorkOrderSummaryFact[]
  contacts: WorkOrderSummaryContact[]
}

type BuildWorkOrderSummaryArgs = {
  workOrder: WorkOrder
  properties?: Property[]
  customers?: Customer[]
  propertyCustomers?: PropertyCustomer[]
  contactPoints?: ContactPoint[]
  scheduleEvents?: ScheduleEvent[]
  scheduleEventCommunicationPresentations?: Record<string, ScheduleEventCommunicationPresentation>
}

const workOrderStatusLabels: Record<WorkOrderStatus | string, string> = {
  OPEN: "Offen",
  COMPLETED: "Abgeschlossen",
  CANCELED: "Erfolglos abgeschlossen",
  NEW: "Neu",
  IN_PLANNING: "In Planung",
  PLANNED: "Geplant",
  IN_AGREEMENT: "In Vereinbarung",
  ANNOUNCED: "Termin angekündigt",
  AGREED: "Termin bestätigt",
  FAILED: "Erfolglos",
}

const workOrderStatusColors: Record<string, StatusColorName> = {
  NEW: "cyan",
  IN_PLANNING: "amber",
  PLANNED: "orange",
  IN_AGREEMENT: "blue",
  ANNOUNCED: "forest",
  AGREED: "emerald",
  OPEN: "amber",
  COMPLETED: "indigo",
  CANCELED: "rose",
  FAILED: "rose",
}

const usableContactPointStatuses = new Set([null, undefined, "ACTIVE"])

const toStringValue = (value: unknown) => (typeof value === "string" ? value.trim() : "")

const normalizedContactPointKind = (value: unknown) => toStringValue(value).toUpperCase()

const formatContactPointValue = (point: ContactPoint) => {
  const rawValue = toStringValue(point.value) || toStringValue(point.normalized_value)

  if (!rawValue) return ""

  return normalizedContactPointKind(point.kind) === "ADDRESS"
    ? formatContactAddress(rawValue)
    : rawValue
}

const formatRoleLabel = (value: unknown) => {
  const raw = toStringValue(value)
  const enumValue = raw.includes("./") ? raw.split("./").at(-1) : raw

  return formatPropertyCustomerRole(normalizePropertyCustomerRole(enumValue))
}

const formatDuration = (value: number | string | null | undefined) => {
  const minutes = Number(value)

  if (!Number.isFinite(minutes) || minutes <= 0) return "Nicht hinterlegt"

  return `${minutes} Minuten`
}

const formatDateOnly = (value: string | null | undefined) => {
  return formatDateOnlyDisplay(value, "Nicht hinterlegt")
}

const formatAppointmentDate = (value: string | null | undefined) => {
  if (!value) return ""

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) return ""

  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed)
}

const findProperty = (workOrder: WorkOrder, properties: Property[]) =>
  properties.find((property) => property.id === workOrder.property_id) ?? null

const findPropertyUnit = (workOrder: WorkOrder, property: Property | null) =>
  property?.units?.find((unit) => unit.id === workOrder.property_unit_id) ?? null

const buildAddressLabel = (property: Property | null) =>
  property ? formatAddressInline(property) || "Unbekannte Liegenschaft" : "Unbekannte Liegenschaft"

const buildUnitLabel = (workOrder: WorkOrder, property: Property | null) => {
  const unit = findPropertyUnit(workOrder, property)

  return (
    (unit ? formatPropertyUnitDisplay(unit, workOrder.property_unit_label) : "") ||
    formatPropertyUnitDisplayForProperty(
      property,
      workOrder.property_unit_id,
      workOrder.property_unit_label,
    ) ||
    ""
  )
}

const buildStatusLabel = (workOrder: WorkOrder, relevantAppointment?: ScheduleEvent | null) => {
  const viewStatus = toStringValue(workOrder.view_status)

  if (viewStatus) return workOrderStatusLabels[viewStatus] ?? "Auftragsstatus unbekannt"

  return workOrderStatusLabels[workOrder.status] ?? "Auftragsstatus unbekannt"
}

const pickRelevantAppointment = (workOrder: WorkOrder, scheduleEvents: ScheduleEvent[]) => {
  const activeEvents = scheduleEvents
    .filter((event) => event.work_order_id === workOrder.id)
    .filter((event) => !["CANCELED", "NOSHOW"].includes(event.status))

  if (!activeEvents.length) return null

  return (
    [...activeEvents].sort((left, right) => {
      const priorityDiff =
        (scheduleEventPriority[right.status] ?? 0) - (scheduleEventPriority[left.status] ?? 0)

      if (priorityDiff !== 0) return priorityDiff

      return new Date(right.start_at).getTime() - new Date(left.start_at).getTime()
    })[0] ?? null
  )
}

const buildAppointmentLabel = (
  workOrder: WorkOrder,
  scheduleEvents: ScheduleEvent[],
  scheduleEventCommunicationPresentations: Record<string, ScheduleEventCommunicationPresentation>,
) => {
  const event = pickRelevantAppointment(workOrder, scheduleEvents)

  if (!event) return "Kein aktiver Termin"

  const statusLabel = buildScheduleEventCommunicationTagPresentation({
    status: event.status,
    event,
    presentation: scheduleEventCommunicationPresentations[event.id] ?? null,
  }).label
  const dateLabel = formatAppointmentDate(event.start_at)

  return dateLabel ? `${statusLabel} · ${dateLabel}` : statusLabel
}

const contactHintForCustomer = (customerId: string, contactPoints: ContactPoint[]) => {
  const kinds = contactChannelsForCustomer(customerId, contactPoints).map(
    (channel) => channel.label,
  )

  return kinds.length ? kinds.join(", ") : "Keine Kontaktinformationen"
}

const contactKindsForCustomer = (customerId: string, contactPoints: ContactPoint[]) => {
  return contactChannelsForCustomer(customerId, contactPoints).map((channel) => channel.label)
}

const contactChannelsForCustomer = (customerId: string, contactPoints: ContactPoint[]) => {
  const channels = new Map<string, WorkOrderSummaryContactChannel>()
  for (const point of contactPoints) {
    if (point.customer_id !== customerId) continue

    if (!usableContactPointStatuses.has(point.lifecycle_status)) continue

    const kind = normalizedContactPointKind(point.kind)

    if (!kind || channels.has(kind)) continue

    const label = contactPointKindLabel(kind)
    const value = formatContactPointValue(point)
    channels.set(kind, {
      kind,
      label,
      tooltip: value ? `${label}: ${value}` : label,
    })
  }

  return Array.from(channels.values())
}

const fallbackContactsFromLinks = (
  workOrder: WorkOrder,
  customers: Customer[],
  propertyCustomers: PropertyCustomer[],
  contactPoints: ContactPoint[],
): WorkOrderSummaryContact[] => {
  const customerById = new Map(customers.map((customer) => [customer.id, customer]))
  const seen = new Set<string>()
  const links = propertyCustomers.filter((link) => {
    if (link.property_id !== workOrder.property_id) return false

    if (!workOrder.property_unit_id) return true

    return link.property_unit_id === workOrder.property_unit_id || link.property_unit_id === null
  })

  return links
    .sort((left, right) => Number(right.is_primary) - Number(left.is_primary))
    .flatMap((link) => {
      if (seen.has(link.customer_id)) return []

      seen.add(link.customer_id)
      const customer = customerById.get(link.customer_id)
      const label =
        customer?.display_name?.trim() || `Ansprechpartner ${link.customer_id.slice(0, 8)}`

      return [
        {
          label,
          roleLabel: formatRoleLabel(link.role),
          contactHint: contactHintForCustomer(link.customer_id, contactPoints),
          contactKinds: contactKindsForCustomer(link.customer_id, contactPoints),
          contactChannels: contactChannelsForCustomer(link.customer_id, contactPoints),
        },
      ]
    })
}

const fallbackContactTagsFromLinks = (
  workOrder: WorkOrder,
  customers: Customer[],
  propertyCustomers: PropertyCustomer[],
  contactPoints: ContactPoint[],
): ContactTagPresentation[] => {
  const customerById = new Map(customers.map((customer) => [customer.id, customer]))
  const contactPointsByCustomerId = groupContactPointsByCustomerId(contactPoints)
  const seen = new Set<string>()
  const links = propertyCustomers.filter((link) => {
    if (link.property_id !== workOrder.property_id) return false

    if (!workOrder.property_unit_id) return true

    return link.property_unit_id === workOrder.property_unit_id || link.property_unit_id === null
  })

  return links
    .sort((left, right) => Number(right.is_primary) - Number(left.is_primary))
    .flatMap((link) => {
      if (seen.has(link.customer_id)) return []

      seen.add(link.customer_id)
      const customer = customerById.get(link.customer_id)
      const fallbackLabel =
        customer?.display_name?.trim() || `Ansprechpartner ${link.customer_id.slice(0, 8)}`

      return [
        buildContactTag({
          id: link.customer_id || `${workOrder.id}-contact`,
          customer,
          fallbackLabel,
          contactPoints: contactPointsByCustomerId[link.customer_id] ?? [],
          roleLabel: formatRoleLabel(link.role),
        }),
      ]
    })
}

const buildContacts = ({
  workOrder,
  customers = [],
  propertyCustomers = [],
  contactPoints = [],
}: Pick<
  BuildWorkOrderSummaryArgs,
  "workOrder" | "customers" | "propertyCustomers" | "contactPoints"
>) => {
  const recipients = workOrder.recipients ?? []

  if (recipients.length) {
    return recipients.map((recipient) => {
      const label =
        recipient.display_name?.trim() ||
        recipient.full_name?.trim() ||
        `Ansprechpartner ${String(recipient.customer_id).slice(0, 8)}`

      return {
        label,
        roleLabel: formatRoleLabel(recipient.role),
        contactHint: contactHintForCustomer(recipient.customer_id, contactPoints),
        contactKinds: contactKindsForCustomer(recipient.customer_id, contactPoints),
        contactChannels: contactChannelsForCustomer(recipient.customer_id, contactPoints),
      }
    })
  }

  return fallbackContactsFromLinks(workOrder, customers, propertyCustomers, contactPoints)
}

const buildContactTags = ({
  workOrder,
  customers = [],
  propertyCustomers = [],
  contactPoints = [],
}: Pick<
  BuildWorkOrderSummaryArgs,
  "workOrder" | "customers" | "propertyCustomers" | "contactPoints"
>) => {
  const contactPointsByCustomerId = groupContactPointsByCustomerId(contactPoints)
  const customerById = new Map(customers.map((customer) => [customer.id, customer]))
  const recipients = workOrder.recipients ?? []

  if (recipients.length) {
    return recipients.map((recipient, index) =>
      buildContactTag({
        id: recipient.customer_id || `${workOrder.id}-recipient-${index}`,
        customer: customerById.get(recipient.customer_id),
        fallbackLabel: recipient.display_name ?? recipient.full_name ?? null,
        contactPoints: contactPointsByCustomerId[recipient.customer_id] ?? [],
        roleLabel: formatRoleLabel(recipient.role),
      }),
    )
  }

  return fallbackContactTagsFromLinks(workOrder, customers, propertyCustomers, contactPoints)
}

const buildStatusTag = (
  workOrder: WorkOrder,
  scheduleEvents: ScheduleEvent[],
): StatusDefinition<string> => {
  const relevantAppointment = pickRelevantAppointment(workOrder, scheduleEvents)
  const statusCode = toStringValue(workOrder.view_status) || workOrder.status

  if (relevantAppointment && ["ANNOUNCED", "AGREED"].includes(statusCode)) {
    const presentation = buildScheduleEventCommunicationTagPresentation({
      status: relevantAppointment.status,
      event: relevantAppointment,
    })

    return { ...presentation.definition, value: statusCode }
  }

  return {
    value: statusCode,
    label: buildStatusLabel(workOrder, relevantAppointment),
    color: workOrderStatusColors[statusCode] ?? "amber",
    icon:
      relevantAppointment?.status === "ANNOUNCED"
        ? mdiEyeCheckOutline
        : mdiClipboardTextClockOutline,
    description: "Aktueller Auftragsstatus.",
  }
}

const buildAppointmentTag = (
  workOrder: WorkOrder,
  scheduleEvents: ScheduleEvent[],
  scheduleEventCommunicationPresentations: Record<string, ScheduleEventCommunicationPresentation>,
): StatusDefinition<string> => {
  const event = pickRelevantAppointment(workOrder, scheduleEvents)

  if (!event) {
    return {
      value: "NONE",
      label: "Kein aktiver Termin",
      color: "rose",
      icon: mdiCalendarClockOutline,
      description: "Für diesen Auftrag ist kein aktiver Termin hinterlegt.",
    }
  }

  const presentation = buildScheduleEventCommunicationTagPresentation({
    status: event.status,
    event,
    presentation: scheduleEventCommunicationPresentations[event.id] ?? null,
  })

  return {
    ...presentation.definition,
    label: buildAppointmentLabel(
      workOrder,
      scheduleEvents,
      scheduleEventCommunicationPresentations,
    ),
  }
}

const buildDurationTag = (workOrder: WorkOrder): StatusDefinition<string> => {
  const label = formatDuration(workOrder.estimated_duration_min)

  return {
    value: "DURATION",
    label,
    color: label === "Nicht hinterlegt" ? "amber" : "cyan",
    icon: mdiClockOutline,
    description: "Geplante Dauer.",
  }
}

const buildDueTag = (workOrder: WorkOrder): StatusDefinition<string> => {
  const label = formatDateOnly(workOrder.due_date)

  return {
    value: "DUE",
    label: label === "Nicht hinterlegt" ? "Frist nicht hinterlegt" : label,
    color: label === "Nicht hinterlegt" ? "amber" : "emerald",
    icon: mdiClockOutline,
    description: "Gesetzliche Frist.",
  }
}

export const buildWorkOrderSummary = ({
  workOrder,
  properties = [],
  customers = [],
  propertyCustomers = [],
  contactPoints = [],
  scheduleEvents = [],
  scheduleEventCommunicationPresentations = {},
}: BuildWorkOrderSummaryArgs): WorkOrderSummaryModel => {
  const property = findProperty(workOrder, properties)
  const unitLabel = buildUnitLabel(workOrder, property)
  const orderTypeTitle = internalOrderTypeLabel(workOrder)
  const addressLabel = buildAddressLabel(property)
  const durationLabel = formatDuration(workOrder.estimated_duration_min)
  const dueDateLabel = formatDateOnly(workOrder.due_date)
  const relevantAppointment = pickRelevantAppointment(workOrder, scheduleEvents)
  const appointmentLabel = buildAppointmentLabel(
    workOrder,
    scheduleEvents,
    scheduleEventCommunicationPresentations,
  )
  const statusLabel = buildStatusLabel(workOrder, relevantAppointment)
  const orderTypeFactLabel = orderTypeHeadingLabel(workOrder, "Auftragstyp")
  const orderFacts: WorkOrderSummaryFact[] = [
    { label: "Auftragsnummer", value: workOrder.order_number?.trim() || "Ohne Auftragsnummer" },
    { label: orderTypeFactLabel, value: orderTypeTitle || "Kein Auftragstyp" },
    { label: "Status", value: statusLabel },
    { label: "Terminlage", value: appointmentLabel },
    { label: "Dauer", value: durationLabel },
  ]
  const locationFacts: WorkOrderSummaryFact[] = [
    { label: "Liegenschaft", value: addressLabel },
    { label: "Ziel", value: unitLabel || "Keine Gebäude- oder Wohneinheit" },
  ]
  const contacts = buildContacts({ workOrder, customers, propertyCustomers, contactPoints })
  const facts: WorkOrderSummaryFact[] = [
    { label: "Auftragsnummer", value: workOrder.order_number?.trim() || "Ohne Auftragsnummer" },
    { label: orderTypeFactLabel, value: orderTypeTitle || "Kein Auftragstyp" },
    { label: "Status", value: statusLabel },
    { label: "Liegenschaft", value: addressLabel },
    { label: "Geplante Dauer", value: durationLabel },
    { label: "Gesetzliche Frist", value: dueDateLabel },
    { label: "Terminlage", value: appointmentLabel },
  ]

  return {
    orderTag: {
      label: orderTypeTitle || "Ohne Typ",
      numberLabel: workOrder.order_number?.trim() || "–",
      color: resolveOrderTypeColor(orderTypeTitle),
    },
    statusTag: buildStatusTag(workOrder, scheduleEvents),
    appointmentTag: buildAppointmentTag(
      workOrder,
      scheduleEvents,
      scheduleEventCommunicationPresentations,
    ),
    durationTag: buildDurationTag(workOrder),
    dueTag: buildDueTag(workOrder),
    propertyLabel: addressLabel,
    unitLabel: unitLabel || null,
    contactTags: buildContactTags({ workOrder, customers, propertyCustomers, contactPoints }),
    orderFacts,
    locationFacts,
    contactRows: contacts,
    facts,
    contacts,
  }
}
